import React, { useState, useMemo } from 'react';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { Ticket as TicketType } from '../../types';
import { api } from '../../utils/api';
import { Send } from 'lucide-react';
import toast from 'react-hot-toast';

export const TicketManagement: React.FC = () => {
    const { tickets, addTicketReply, updateTicketStatus } = useData();
    const { user } = useAuth();
    const [selectedTicket, setSelectedTicket] = useState<any | null>(null);
    const [replyMessage, setReplyMessage] = useState('');
    const [isLoadingTicket, setIsLoadingTicket] = useState(false);

    const openTickets = useMemo(() => tickets.filter(t => t.status === 'abierto').sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()), [tickets]);
    const inProgressTickets = useMemo(() => tickets.filter(t => t.status === 'en_progreso').sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()), [tickets]);
    const closedTickets = useMemo(() => tickets.filter(t => t.status === 'cerrado').sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()), [tickets]);

    const handleSelectTicket = async (ticket: TicketType) => {
        if (selectedTicket?.id === ticket.id) {
            setSelectedTicket(null);
            return;
        }
        setIsLoadingTicket(true);
        try {
            const detailedTicket = await api.getTicketDetails(ticket.id);
            setSelectedTicket(detailedTicket);
        } catch (error) {
            toast.error('Error al cargar los detalles del ticket.');
        } finally {
            setIsLoadingTicket(false);
        }
    };

    const handleReplySubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!replyMessage.trim() || !selectedTicket || !user) return;

        const originalTicketId = selectedTicket.id;
        const toastId = toast.loading('Enviando respuesta...');
        try {
            await addTicketReply(originalTicketId, {
                authorId: user.id,
                authorRole: 'admin',
                message: replyMessage,
            });
            setReplyMessage('');
            const updatedTicket = await api.getTicketDetails(originalTicketId);
            setSelectedTicket(updatedTicket);
            toast.success('Respuesta enviada.', { id: toastId });
        } catch (error) {
            toast.error('No se pudo enviar la respuesta.', { id: toastId });
        }
    };
    
    const handleStatusChange = async (ticketId: string, newStatus: 'abierto' | 'en_progreso' | 'cerrado') => {
        const toastId = toast.loading('Actualizando estado...');
        try {
            await updateTicketStatus(ticketId, newStatus);
            if (selectedTicket?.id === ticketId) {
                setSelectedTicket({ ...selectedTicket, status: newStatus });
            }
            toast.success('Estado del ticket actualizado.', { id: toastId });
        } catch (error) {
            toast.error('Error al actualizar el estado.', { id: toastId });
        }
    };
    
    const getPriorityColor = (priority: 'bajo' | 'medio' | 'alto') => {
        if (priority === 'alto') return 'bg-red-500';
        if (priority === 'medio') return 'bg-yellow-400';
        return 'bg-green-500';
    };

    const TicketCard: React.FC<{ ticket: TicketType }> = ({ ticket }) => (
        <div onClick={() => handleSelectTicket(ticket)} className="bg-white p-3 rounded-lg border shadow-sm cursor-pointer hover:shadow-md hover:border-blue-500 transition-all">
            <div className="flex justify-between items-start">
                <p className="font-bold text-gray-800 text-sm">{ticket.subject}</p>
                <span className={`w-3 h-3 rounded-full flex-shrink-0 ${getPriorityColor(ticket.priority)}`} title={`Prioridad: ${ticket.priority}`}></span>
            </div>
            <p className="text-sm text-gray-600 mt-1">{ticket.clientName}</p>
            <p className="text-xs text-gray-400 mt-2 text-right">{new Date(ticket.updatedAt).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
        </div>
    );

    return (
        <div className="h-full">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-full">
                {/* Columna Abierto */}
                <div className="flex flex-col bg-white rounded-xl border">
                    <h3 className="font-bold text-gray-700 p-4 border-b bg-gray-50 rounded-t-xl">Abierto ({openTickets.length})</h3>
                    <div className="p-4 space-y-4 overflow-y-auto flex-1">{openTickets.map(ticket => <TicketCard key={ticket.id} ticket={ticket} />)}</div>
                </div>
                {/* Columna En Progreso */}
                <div className="flex flex-col bg-white rounded-xl border">
                    <h3 className="font-bold text-gray-700 p-4 border-b bg-gray-50 rounded-t-xl">En Progreso ({inProgressTickets.length})</h3>
                    <div className="p-4 space-y-4 overflow-y-auto flex-1">{inProgressTickets.map(ticket => <TicketCard key={ticket.id} ticket={ticket} />)}</div>
                </div>
                {/* Columna Cerrado */}
                <div className="flex flex-col bg-white rounded-xl border">
                    <h3 className="font-bold text-gray-700 p-4 border-b bg-gray-50 rounded-t-xl">Cerrado ({closedTickets.length})</h3>
                    <div className="p-4 space-y-4 overflow-y-auto flex-1">{closedTickets.map(ticket => <TicketCard key={ticket.id} ticket={ticket} />)}</div>
                </div>
            </div>

            {/* Modal para ver el detalle del Ticket */}
            {selectedTicket && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50" onClick={() => setSelectedTicket(null)}>
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col h-3/4" onClick={(e) => e.stopPropagation()}>
                        <div className="p-4 border-b flex justify-between items-center">
                            <div>
                                <h2 className="text-xl font-bold">{selectedTicket.subject}</h2>
                                <p className="text-sm text-gray-500">De: {selectedTicket.clientName} | Prioridad: <span className="capitalize font-medium">{selectedTicket.priority}</span></p>
                            </div>
                            <select value={selectedTicket.status} onChange={(e) => handleStatusChange(selectedTicket.id, e.target.value as any)} className="border-gray-300 rounded-md shadow-sm">
                                <option value="abierto">Abierto</option>
                                <option value="en_progreso">En Progreso</option>
                                <option value="cerrado">Cerrado</option>
                            </select>
                        </div>
                        <div className="flex-1 p-6 space-y-4 overflow-y-auto bg-gray-50">
                            {selectedTicket.replies?.map((reply: any) => (
                                <div key={reply.id} className={`flex ${reply.authorRole === 'admin' ? 'justify-start' : 'justify-end'}`}>
                                    <div className={`max-w-xl p-3 rounded-lg shadow-sm ${reply.authorRole === 'admin' ? 'bg-slate-200 text-gray-800' : 'bg-blue-500 text-white'}`}>
                                        <p className="text-sm font-semibold mb-1">{reply.authorRole === 'admin' ? 'Soporte (Tú)' : selectedTicket.clientName}</p>
                                        <p className="text-sm whitespace-pre-wrap">{reply.message}</p>
                                        <p className="text-xs opacity-75 mt-1 text-right">{new Date(reply.createdAt).toLocaleString('es-ES', { day:'numeric', month:'short', hour: '2-digit', minute:'2-digit'})}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {selectedTicket.status !== 'cerrado' && (
                            <div className="p-4 border-t bg-white">
                                <form onSubmit={handleReplySubmit} className="flex space-x-3">
                                    <input type="text" value={replyMessage} onChange={(e) => setReplyMessage(e.target.value)} placeholder="Escribe tu respuesta..." className="flex-1 p-2 border rounded-md focus:ring-blue-500 focus:border-blue-500" />
                                    <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 flex items-center"><Send className="w-5 h-5" /></button>
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
